<template>
  <section class="home-hero" aria-labelledby="home-hero-title">
    <img
      class="home-hero__image"
      src="@/assets/images/octopus-offshore-structure-hero.jpg"
      alt="Divers working around an Octopus underwater camera"
      fetchpriority="high"
      decoding="async"
    >
    <span class="home-hero__shade" aria-hidden="true"></span>

    <div class="home-hero__inner">
      <div class="home-hero__primary">
        <h1 id="home-hero-title">See What Happens Beneath the Surface</h1>
        <p>
          Fathom Ocean delivers AI-powered underwater monitoring systems that help
          organizations and individuals observe, protect, and understand underwater
          environments in real time.
        </p>

        <div class="home-hero__actions">
          <div
            class="home-hero__dropdown"
            :class="{ 'is-open': openMenu === 'cameras' }"
            @focusout="handleFocusOut"
            @keyup.esc="closeMenus"
          >
            <b-button
              aria-haspopup="true"
              :aria-expanded="openMenu === 'cameras' ? 'true' : 'false'"
              @click="toggleMenu('cameras')"
            >
              Watch Live Cameras
            </b-button>

            <div class="home-hero__menu">
              <router-link to="/live-cameras/coral-city">Coral City Camera</router-link>
              <router-link to="/live-cameras/wahoo-bay">Wahoo Bay</router-link>
              <router-link to="/live-cameras/okaloosa-island-pier">Okaloosa Island Pier</router-link>
              <router-link to="/live-cameras/mega-lab-hawaii-1">MEGA Lab Hawaii</router-link>
            </div>
          </div>

          <div
            class="home-hero__dropdown home-hero__dropdown--quiet"
            :class="{ 'is-open': openMenu === 'products' }"
            @focusout="handleFocusOut"
            @keyup.esc="closeMenus"
          >
            <b-button
              aria-haspopup="true"
              :aria-expanded="openMenu === 'products' ? 'true' : 'false'"
              @click="toggleMenu('products')"
            >
              Products
            </b-button>

            <div class="home-hero__menu">
              <router-link to="/products/Angler">Angler™</router-link>
              <router-link to="/products/octopus">Octopus™</router-link>
              <router-link to="/products/periscope">Periscope AI™</router-link>
              <router-link to="/products/comparesystems">Compare Systems</router-link>
            </div>
          </div>
        </div>
      </div>

      <p class="home-hero__secondary">
        From marine research and conservation to infrastructure monitoring, public
        engagement, and waterfront living, our self-cleaning camera systems provide
        a reliable window into the world below the surface.
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HomeHero',
  data() {
    return {
      openMenu: null,
    }
  },
  methods: {
    toggleMenu(menu) {
      this.openMenu = this.openMenu === menu ? null : menu
    },
    closeMenus() {
      this.openMenu = null
    },
    handleFocusOut(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) this.closeMenus()
    },
  },
}
</script>

<style lang="scss">
#app .home-hero {
  position: relative;
  min-height: max(760px, calc(100svh - var(--header-height)));
  overflow: hidden;
  isolation: isolate;
  color: var(--white);
  background: var(--near-black);
}

#app .home-hero__image,
#app .home-hero__shade {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

#app .home-hero__image {
  z-index: -2;
  object-fit: cover;
  object-position: 50% 38%;
  filter: saturate(0.92) contrast(1.04) brightness(0.82);
  transform: scale(1.006);
}

#app .home-hero__shade {
  z-index: -1;
  background:
    linear-gradient(90deg, rgba(3, 9, 20, 0.9) 0%, rgba(3, 9, 20, 0.6) 37%, rgba(3, 9, 20, 0.12) 68%, rgba(3, 9, 20, 0.03)),
    linear-gradient(180deg, rgba(3, 9, 20, 0.08), rgba(3, 9, 20, 0.03) 58%, rgba(3, 9, 20, 0.54));
}

#app .home-hero__inner {
  display: grid;
  width: min(calc(100% - 80px), var(--page));
  min-height: max(760px, calc(100svh - var(--header-height)));
  margin: 0 auto;
  padding: clamp(96px, 11vh, 150px) 0 70px;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 24px;
  align-items: center;
}

#app .home-hero__primary {
  grid-column: 1 / span 7;
}

#app .home-hero h1 {
  max-width: 760px;
  margin-bottom: 30px;
  font-size: clamp(4.2rem, 6.8vw, 6.5rem);
  font-weight: 600;
  letter-spacing: -0.052em;
  line-height: 0.91;
  text-wrap: balance;
}

#app .home-hero__primary > p {
  max-width: 590px;
  margin: 0;
  color: #d7e0e5;
  font-size: clamp(1.12rem, 1.35vw, 1.3rem);
  line-height: 1.62;
}

#app .home-hero__secondary {
  align-self: end;
  max-width: 490px;
  margin: 0 0 16px;
  padding-top: 24px;
  border-top: 1px solid rgba(207, 237, 248, 0.28);
  grid-column: 9 / -1;
  color: #d7e0e5;
  font-size: 1.13rem;
  line-height: 1.62;
}

#app .home-hero__actions {
  display: flex;
  margin-top: 34px;
  gap: 26px;
  align-items: center;
  flex-wrap: wrap;
}

#app .home-hero__dropdown {
  position: relative;
}

#app .home-hero__dropdown .button {
  min-height: 52px;
  padding: 0 24px;
  border-color: var(--coral) !important;
  color: var(--near-black) !important;
  background: var(--coral) !important;
}

#app .home-hero__dropdown--quiet .button {
  padding-right: 7px;
  padding-left: 0;
  border: 0 !important;
  color: var(--white) !important;
  background: transparent !important;
  box-shadow: none !important;
}

#app .home-hero__dropdown--quiet .button::after {
  margin-left: 10px;
  content: "→";
  transition: transform 180ms ease;
}

#app .home-hero__dropdown--quiet .button:hover::after,
#app .home-hero__dropdown--quiet .button:focus-visible::after {
  transform: translateX(4px);
}

#app .home-hero__menu {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 20;
  display: grid;
  min-width: 300px;
  padding: 9px;
  border: 1px solid rgba(207, 237, 248, 0.15);
  visibility: hidden;
  background: rgba(3, 9, 20, 0.985);
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.42);
  opacity: 0;
  transform: translateY(-5px);
  transition: opacity 150ms ease, transform 170ms ease, visibility 170ms;
}

#app .home-hero__dropdown:hover .home-hero__menu,
#app .home-hero__dropdown:focus-within .home-hero__menu,
#app .home-hero__dropdown.is-open .home-hero__menu {
  visibility: visible;
  opacity: 1;
  transform: translateY(0);
}

#app .home-hero__menu a {
  min-height: 48px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(207, 237, 248, 0.1);
  color: var(--white);
  font-size: 1rem;
  line-height: 1.3;
  text-decoration: none;
}

#app .home-hero__menu a:last-child {
  border-bottom: 0;
}

#app .home-hero__menu a:hover,
#app .home-hero__menu a:focus-visible {
  color: var(--pale-blue);
}

@media (prefers-reduced-motion: no-preference) {
  #app .home-hero__image {
    animation: home-hero-drift 18s ease-in-out infinite alternate;
  }
}

@keyframes home-hero-drift {
  from { transform: scale(1.006); }
  to { transform: scale(1.02); }
}

@media (max-width: 1024px) {
  #app .home-hero__image {
    object-position: 52% 44%;
  }

  #app .home-hero__inner {
    width: calc(100% - 60px);
  }

  #app .home-hero__primary {
    grid-column: 1 / span 8;
  }

  #app .home-hero__secondary {
    grid-column: 8 / -1;
  }
}

@media (max-width: 780px) {
  #app .home-hero {
    min-height: 820px;
  }

  #app .home-hero__image {
    object-position: 43% 50%;
    filter: saturate(0.94) contrast(1.04) brightness(0.84);
  }

  #app .home-hero__shade {
    background:
      linear-gradient(180deg, rgba(3, 9, 20, 0.82) 0%, rgba(3, 9, 20, 0.5) 42%, rgba(3, 9, 20, 0.12) 67%, rgba(3, 9, 20, 0.64)),
      linear-gradient(90deg, rgba(3, 9, 20, 0.4), transparent 76%);
  }

  #app .home-hero__inner {
    display: flex;
    width: calc(100% - 40px);
    min-height: 820px;
    padding: 74px 0 38px;
    flex-direction: column;
    align-items: stretch;
  }

  #app .home-hero__primary {
    width: 100%;
  }

  #app .home-hero h1 {
    max-width: 9ch;
    margin-bottom: 24px;
    font-size: clamp(3.45rem, 15.4vw, 5rem);
  }

  #app .home-hero__primary > p {
    max-width: 520px;
    font-size: 1.06rem;
  }

  #app .home-hero__secondary {
    max-width: 520px;
    margin-top: auto;
    font-size: 1rem;
  }

  #app .home-hero__menu {
    min-width: min(300px, calc(100vw - 40px));
  }
}

@media (max-width: 430px) {
  #app .home-hero__actions {
    gap: 18px;
  }

  #app .home-hero__dropdown .button {
    min-height: 48px;
    padding: 0 17px;
    font-size: 0.9rem;
  }

  #app .home-hero__dropdown--quiet .button {
    padding-left: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  #app .home-hero__image,
  #app .home-hero__menu,
  #app .home-hero__dropdown--quiet .button::after {
    animation: none;
    transition: none;
  }
}
</style>
